import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import axiosConfig from "../../AxiosConfig";
import {
  Bell,
  Boxes,
  ChevronRight,
  Gem,
  LayoutGrid,
  Moon,
  Palette,
  RotateCcw,
  Search,
  Settings2,
  Sparkles,
  Sun,
  UserCircle2,
} from "lucide-react";
import { quickActionGroups, topNav } from "../../layout/navConfig";
import { useTheme } from "../../context/ThemeContext";
import logout from "../../pages/Logout";

const PRESET_COLORS = [
  "#5b7cff",
  "#7c3aed",
  "#0f9d7a",
  "#ef6b4a",
  "#0ea5e9",
  "#db2777",
  "#14b8a6",
  "#f59e0b",
];

const SEARCH_ITEMS = quickActionGroups.flatMap((group) => [
  {
    label: group.label,
    path: group.path,
    category: "Module",
  },
  ...group.sections.map((section) => ({
    label: `${group.label} • ${section.label}`,
    path: section.path,
    category: "Function",
  })),
]);

const COLOR_FIELDS = [
  { key: "background", label: "Page background" },
  { key: "backgroundSoft", label: "Soft background" },
  { key: "card", label: "Main card" },
  { key: "subCard", label: "Sub card" },
  { key: "text", label: "Main text" },
  { key: "subText", label: "Secondary text" },
  { key: "border", label: "Border" },
  { key: "topBar", label: "Top bar background" },
  { key: "topBarText", label: "Top bar text" },
  { key: "tab", label: "Tab background" },
  { key: "tabText", label: "Tab text" },
  { key: "tabActive", label: "Active tab background" },
  { key: "tabActiveText", label: "Active tab text" },
];

const injectHeaderStyles = () => {
  if (document.getElementById("studio-top-header-dynamic-css")) return;

  const style = document.createElement("style");
  style.id = "studio-top-header-dynamic-css";
  style.textContent = `
 .pro-top-header{
  position: sticky;
  top: 0;
  z-index: 1200;
  padding: 0;
  margin: 0;
  background: transparent;
}

.pro-top-inner{
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 72px;
  padding: 10px 12px;
  border-radius: 0;
  border: 1px solid var(--app-border);
  background: var(--app-top-bar);
  box-shadow:
    0 18px 42px rgba(15, 23, 42, 0.08),
    0 10px 24px var(--app-accent-shadow);
  color: var(--app-top-bar-text);
  transition: background 0.25s ease, border-color 0.25s ease, color 0.25s ease;
}

    .pro-brand-link{
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      text-decoration: none;
    }

    .pro-brand-logo{
      width: 48px;
      height: 48px;
      border-radius: 16px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 1px solid var(--app-accent-border);
      background:
        radial-gradient(circle at 30% 20%, rgba(255,255,255,0.95), transparent 45%),
        linear-gradient(135deg, var(--app-accent-soft-strong), var(--app-card));
      color: var(--app-accent);
      box-shadow: 0 10px 22px var(--app-accent-shadow);
    }

    .pro-tabs-wrap{
      flex: 0 1 auto;
      min-width: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }

    .pro-top-nav{
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none;
      -ms-overflow-style: none;
      padding: 2px 2px 2px 0;
    }

    .pro-top-nav::-webkit-scrollbar{
      display: none;
    }

    .pro-top-link{
      text-decoration: none;
      flex: 0 0 auto;
    }

    .pro-top-item{
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 11px 14px;
      border-radius: 14px;
      border: 1px solid transparent;
      background: var(--app-tab);
      color: var(--app-tab-text);
      font-size: 13px;
      font-weight: 600;
      white-space: nowrap;
      transition: 0.22s ease;
    }

    .pro-top-item:hover{
      border-color: var(--app-accent-border);
      background: var(--app-accent-soft);
      color: var(--app-accent);
    }

    .pro-top-item.active{
      background: var(--app-tab-active);
      color: var(--app-tab-active-text);
      border-color: transparent;
      box-shadow: 0 14px 28px var(--app-accent-shadow-strong);
    }

    .pro-top-right{
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      gap: 10px;
      position: relative;
    }

    .pro-search-shell,
    .pro-settings-shell{
      position: relative;
      flex: 0 0 auto;
    }

    .pro-search-box{
      display: flex;
      align-items: center;
      gap: 8px;
      height: 46px;
      overflow: hidden;
      border-radius: 16px;
      border: 1px solid var(--app-border);
      background: var(--app-card);
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.45);
    }

    .pro-icon-btn{
      width: 46px;
      height: 46px;
      border: none;
      outline: none;
      cursor: pointer;
      border-radius: 16px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      color: var(--app-top-bar-text);
      transition: 0.2s ease;
      flex: 0 0 46px;
    }

    .pro-icon-btn:hover{
      color: var(--app-accent);
      background: var(--app-accent-soft);
    }

    .pro-icon-btn.pro-settings-btn{
      background: linear-gradient(135deg, var(--app-accent-soft-strong), var(--app-card));
      border: 1px solid var(--app-accent-border);
      color: var(--app-accent);
      box-shadow: 0 10px 22px var(--app-accent-shadow);
    }

    .pro-inline-search{
      flex: 1 1 auto;
      min-width: 0;
      height: 100%;
      border: none;
      outline: none;
      background: transparent;
      color: var(--app-text);
      font-size: 13px;
      font-weight: 500;
      padding: 0 12px 0 0;
    }

    .pro-inline-search::placeholder{
      color: var(--app-subtext);
    }

    .pro-search-dropdown,
    .pro-settings-card{
      position: absolute;
      top: calc(100% + 12px);
      right: 0;
      border-radius: 20px;
      border: 1px solid var(--app-border);
      background: var(--app-card);
      box-shadow:
        0 28px 60px rgba(15, 23, 42, 0.18),
        0 10px 20px rgba(15, 23, 42, 0.08);
    }

    .pro-search-dropdown{
      width: 360px;
      max-width: min(360px, calc(100vw - 36px));
      padding: 8px;
    }

    .pro-search-result{
      width: 100%;
      border: none;
      cursor: pointer;
      text-align: left;
      padding: 12px 12px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      background: transparent;
      color: var(--app-text);
      transition: 0.18s ease;
    }

    .pro-search-result:hover{
      background: var(--app-accent-soft);
      color: var(--app-accent);
    }

    .pro-search-result span{
      display: block;
      font-size: 13px;
      font-weight: 700;
      line-height: 1.25;
    }

    .pro-search-result small{
      display: block;
      margin-top: 4px;
      font-size: 11px;
      color: var(--app-subtext);
      font-weight: 600;
    }

    .pro-search-empty{
      padding: 14px 12px;
      font-size: 13px;
      color: var(--app-subtext);
      text-align: center;
    }

    .pro-settings-card{
      width: min(560px, calc(100vw - 28px));
      max-height: calc(100vh - 110px);
      overflow: auto;
      padding: 14px;
    }

    .pro-settings-section{
      padding: 14px;
      border-radius: 18px;
      background: var(--app-sub-card);
      border: 1px solid var(--app-border);
    }

    .pro-settings-section + .pro-settings-section{
      margin-top: 12px;
    }

    .pro-settings-title{
      font-size: 12px;
      font-weight: 800;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--app-subtext);
      margin-bottom: 12px;
    }

    .pro-settings-row{
      width: 100%;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 12px 14px;
      border-radius: 14px;
      background: var(--app-card);
      color: var(--app-text);
      font-size: 13px;
      font-weight: 700;
      margin-bottom: 10px;
      border: 1px solid transparent;
      transition: 0.18s ease;
    }

    .pro-settings-row:hover{
      color: var(--app-accent);
      border-color: var(--app-accent-border);
      background: var(--app-accent-soft);
    }

    .pro-settings-row.static{
      cursor: default;
    }

    .pro-settings-row.static:hover{
      color: var(--app-text);
      border-color: transparent;
      background: var(--app-card);
    }

    .pro-settings-left{
      display: inline-flex;
      align-items: center;
      gap: 10px;
    }

    .pro-settings-row strong,
    .pro-settings-row small{
      color: var(--app-subtext);
      font-weight: 700;
    }

    .pro-color-grid{
      display: grid;
      grid-template-columns: repeat(8, minmax(0, 1fr));
      gap: 10px;
      margin-bottom: 12px;
    }

    .pro-color-dot{
      width: 100%;
      aspect-ratio: 1/1;
      border-radius: 999px;
      border: 3px solid transparent;
      cursor: pointer;
      outline: none;
      transition: 0.18s ease;
      box-shadow: inset 0 0 0 1px rgba(255,255,255,0.85);
    }

    .pro-color-dot.active{
      transform: scale(1.08);
      border-color: var(--app-text);
      box-shadow: 0 0 0 4px var(--app-accent-soft-strong);
    }

    .pro-custom-color-row{
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 14px;
      border-radius: 14px;
      background: var(--app-card);
      border: 1px solid var(--app-border);
      color: var(--app-text);
      font-size: 13px;
      font-weight: 700;
    }

    .pro-custom-color-row input{
      margin-left: auto;
      width: 40px;
      height: 32px;
      border: none;
      background: transparent;
      cursor: pointer;
    }

    .pro-settings-grid{
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }

    .pro-chip{
      border: 1px solid var(--app-border);
      background: var(--app-card);
      color: var(--app-text);
      cursor: pointer;
      border-radius: 999px;
      padding: 10px 14px;
      font-size: 12px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      transition: 0.18s ease;
    }

    .pro-chip:hover{
      background: var(--app-accent-soft);
      border-color: var(--app-accent-border);
      color: var(--app-accent);
    }

    .pro-chip.subtle{
      border-radius: 14px;
    }

    .pro-module-list{
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .pro-module-card{
      border-radius: 16px;
      border: 1px solid var(--app-border);
      background: var(--app-card);
      padding: 12px;
    }

    .pro-module-head{
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 10px;
      color: var(--app-text);
      font-size: 13px;
      font-weight: 800;
    }

    .pro-palette-grid{
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;
    }

    .pro-palette-control{
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 12px;
      border-radius: 14px;
      background: var(--app-card);
      border: 1px solid var(--app-border);
    }

    .pro-palette-label{
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0;
    }

    .pro-palette-label strong{
      font-size: 12px;
      font-weight: 800;
      color: var(--app-text);
    }

    .pro-palette-label small{
      font-size: 11px;
      color: var(--app-subtext);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 120px;
    }

    .pro-palette-picker{
      width: 42px;
      height: 34px;
      border: none;
      background: transparent;
      cursor: pointer;
      flex: 0 0 auto;
    }

    .pro-reset-btn{
      width: 100%;
      margin-top: 12px;
      border: 1px solid var(--app-accent-border);
      background: var(--app-accent-soft);
      color: var(--app-accent);
      padding: 12px 14px;
      border-radius: 14px;
      font-size: 13px;
      font-weight: 800;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      cursor: pointer;
      transition: 0.2s ease;
    }

    .pro-reset-btn:hover{
      transform: translateY(-1px);
      background: var(--app-accent-soft-strong);
    }

    @media (max-width: 1180px){
      .pro-top-inner{
        gap: 10px;
      }

      .pro-top-nav{
        gap: 6px;
      }

      .pro-top-item{
        padding: 10px 12px;
        font-size: 12px;
      }
    }

    @media (max-width: 900px){
  .pro-top-header{
    padding: 0;
    margin: 0;
  }

  .pro-top-inner{
    padding: 10px;
    gap: 8px;
    border-radius: 0;
  }
}

      .pro-top-item{
        padding: 10px 12px;
      }

      .pro-palette-grid{
        grid-template-columns: 1fr;
      }
    }

    @media (max-width: 640px){
      .pro-brand-logo{
        width: 44px;
        height: 44px;
        border-radius: 14px;
      }

      .pro-icon-btn{
        width: 42px;
        height: 42px;
        flex-basis: 42px;
      }

      .pro-search-box{
        height: 42px;
      }

      .pro-search-dropdown,
      .pro-settings-card{
        right: -6px;
      }
    }
  `;
  document.head.appendChild(style);
};

export default function TopHeader({ activeTop, setIsAuth }) {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const {
    isDark,
    theme,
    setTheme,
    accentColor,
    setAccentColor,
    palette,
    setPaletteColor,
    resetPalette,
  } = useTheme();

  const [query, setQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const searchRef = useRef(null);
  const settingsRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    injectHeaderStyles();
  }, []);
useEffect(() => {
  const handleClickOutside = (event) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target)
    ) {
      setShowAccountMenu(false);
    }
  };

  document.addEventListener(
    "mousedown",
    handleClickOutside
  );

  return () => {
    document.removeEventListener(
      "mousedown",
      handleClickOutside
    );
  };
}, []);
  useEffect(() => {
    const handleClick = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearch(false);
      }
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setShowSettings(false);
      }
    };

    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setShowSearch(false);
        setShowSettings(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, []);

  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 80);
    }
  }, [showSearch]);

  const results = useMemo(() => {
    if (!query.trim()) return SEARCH_ITEMS.slice(0, 10);
    return SEARCH_ITEMS.filter((item) =>
      item.label.toLowerCase().includes(query.toLowerCase()),
    ).slice(0, 10);
  }, [query]);

  const jump = (path) => {
    navigate(path);
    setShowSearch(false);
    setShowSettings(false);
    setQuery("");
  };
  const handleLogout = (navigate) => {
    // ✅ Sirf userData remove
    localStorage.removeItem("userData");

    // (optional) agar loginInfo bhi use ho raha hai
    localStorage.removeItem("loginInfo");

    // (optional) session bhi hata do
    localStorage.removeItem("sessionId");
    setIsAuth(false);
    // 🔁 redirect to login
    navigate("/login", { replace: true });
  };
  return (
    <header className="pro-top-header">
      <div className="pro-top-inner">
        <div className="pro-brand-link" style={{ position: "relative" }}>
          <motion.div
            className="pro-brand-logo"
            whileHover={{ y: -1, scale: 1.02 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => setShowAccountMenu(!showAccountMenu)}
            style={{ cursor: "pointer" }}
          >
            <Gem size={18} />
          </motion.div>

          {/* 🔽 Dropdown */}
          {showAccountMenu && (
            <div
            ref={dropdownRef}
              style={{
                position: "absolute",
                top: "60px",
                left: "0",
                background: "#fff",
                border: "1px solid #ddd",
                borderRadius: "8px",
                padding: "10px",
                width: "150px",
                zIndex: 1000,
              }}
            >
              {/* Home Button */}
              <button
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                }}
                onClick={() => navigate("/")}
              >
                Home
              </button>

              {/* Logout Button */}
              <button
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: "red",
                }}
                onClick={() => handleLogout(navigate, setIsAuth)}
              >
                Logout
              </button>
            </div>
          )}
        </div>
        <div className="pro-tabs-wrap">
          <nav className="pro-top-nav">
            {topNav.map((item) => {
              const Icon = item.icon;
              const active = activeTop === item.key;

              return (
                <Link key={item.key} to={item.path} className="pro-top-link">
                  <motion.span
                    className={`pro-top-item ${active ? "active" : ""}`}
                    whileTap={{ scale: 0.97 }}
                    whileHover={{ y: -1 }}
                  >
                    <Icon size={16} />
                    <span>{item.label}</span>
                  </motion.span>
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="pro-top-right">
          <div className="pro-search-shell" ref={searchRef}>
            <motion.div
              className="pro-search-box"
              animate={{ width: showSearch ? 320 : 46 }}
              transition={{ type: "spring", stiffness: 380, damping: 28 }}
            >
              <motion.button
                type="button"
                className="pro-icon-btn"
                onClick={() => {
                  setShowSearch((prev) => !prev);
                  setShowSettings(false);
                }}
                whileTap={{ scale: 0.95 }}
                title="Search"
              >
                <Search size={18} />
              </motion.button>

              <AnimatePresence>
                {showSearch ? (
                  <motion.input
                    ref={searchInputRef}
                    className="pro-inline-search"
                    placeholder="Search modules, pages and functions..."
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    transition={{ duration: 0.16 }}
                  />
                ) : null}
              </AnimatePresence>
            </motion.div>

            <AnimatePresence>
              {showSearch ? (
                <motion.div
                  className="pro-search-dropdown"
                  initial={{ opacity: 0, y: 12, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.98 }}
                  transition={{ duration: 0.18 }}
                >
                  {results.length ? (
                    results.map((item) => (
                      <motion.button
                        key={`${item.label}-${item.path}`}
                        className="pro-search-result"
                        onClick={() => jump(item.path)}
                        whileTap={{ scale: 0.985 }}
                      >
                        <div>
                          <span>{item.label}</span>
                          <small>{item.category}</small>
                        </div>
                        <ChevronRight size={14} />
                      </motion.button>
                    ))
                  ) : (
                    <div className="pro-search-empty">No result found</div>
                  )}
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>

          <div className="pro-settings-shell" ref={settingsRef}>
            <motion.button
              type="button"
              className="pro-icon-btn pro-settings-btn"
              onClick={() => {
                setShowSettings((prev) => !prev);
                setShowSearch(false);
              }}
              whileTap={{ scale: 0.95 }}
              title="Settings"
            >
              <Settings2 size={18} />
            </motion.button>
            {/* <motion.button
    type="button"
    className="pro-icon-btn"
    onClick={() => navigate("/logout")}
    whileTap={{ scale: 0.95 }}
    title="Logout"
  >
    <logout size={18} />
  </motion.button> */}
            <AnimatePresence>
              {showSettings ? (
                <motion.div
                  className="pro-settings-card"
                  initial={{ opacity: 0, y: 12, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.98 }}
                  transition={{ duration: 0.18 }}
                >
                  <div className="pro-settings-section">
                    <div className="pro-settings-title">Workspace</div>

                    <button
                      className="pro-settings-row"
                      onClick={() =>
                        setTheme(theme === "dark" ? "light" : "dark")
                      }
                    >
                      <span className="pro-settings-left">
                        {isDark ? <Sun size={16} /> : <Moon size={16} />}
                        Interface mode
                      </span>
                      <strong>{theme}</strong>
                    </button>

                    <div className="pro-color-grid">
                      {PRESET_COLORS.map((color) => (
                        <button
                          key={color}
                          className={`pro-color-dot ${accentColor === color ? "active" : ""}`}
                          style={{ background: color }}
                          onClick={() => setAccentColor(color)}
                          title={color}
                        />
                      ))}
                    </div>

                    <label className="pro-custom-color-row">
                      <Palette size={16} />
                      <span>Accent color</span>
                      <input
                        type="color"
                        value={accentColor}
                        onChange={(event) => setAccentColor(event.target.value)}
                      />
                    </label>
                  </div>

                  <div className="pro-settings-section">
                    <div className="pro-settings-title">
                      Dynamic interface colors
                    </div>

                    <div className="pro-palette-grid">
                      {COLOR_FIELDS.map((field) => (
                        <label key={field.key} className="pro-palette-control">
                          <div className="pro-palette-label">
                            <strong>{field.label}</strong>
                            <small>{palette[field.key]}</small>
                          </div>
                          <input
                            className="pro-palette-picker"
                            type="color"
                            value={palette[field.key]}
                            onChange={(event) =>
                              setPaletteColor(field.key, event.target.value)
                            }
                          />
                        </label>
                      ))}
                    </div>

                    <button
                      type="button"
                      className="pro-reset-btn"
                      onClick={resetPalette}
                    >
                      <RotateCcw size={15} />
                      Reset current theme colors
                    </button>
                  </div>

                  <div className="pro-settings-section">
                    <div className="pro-settings-title">Account & alerts</div>

                    <div className="pro-settings-row static">
                      <span className="pro-settings-left">
                        <UserCircle2 size={16} />
                        Factory Admin
                      </span>
                      <small>Main Unit</small>
                    </div>

                    <div className="pro-settings-row static">
                      <span className="pro-settings-left">
                        <Bell size={16} />
                        Notifications
                      </span>
                      <small>3 new</small>
                    </div>

                    <div className="pro-settings-row static">
                      <span className="pro-settings-left">
                        <Sparkles size={16} />
                        Experience
                      </span>
                      <small>Fully dynamic colors</small>
                    </div>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </header>
  );
}
